import React, { useEffect, useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { ArrowUp, ArrowDown, ArrowUpDown, Download } from 'lucide-react';
import { getCachedPPRates, getCachedPSRates, getCachedPVCRates } from '@/services/marketIntelService';
import { keepFirstEntryPerMonth } from '@/utils/commonUtils';
import { format } from 'date-fns';
import { createVerticalHoverLinePlugin, createAreaBetweenLinesPlugin } from '@/utils/chartPlugins';
import { useRsColors } from '@/hooks/useRsColors';
import { useTheme } from '@/contexts/ThemeContext';
import ChartDateLimiter from '@/components/charts/ChartDateLimiter';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

type SortColumn = 'date' | 'demand_rates' | 'operating_rates' | 'export_rates' | 'inventory';
type SortDirection = 'asc' | 'desc';

const SHADING = {
  rowEven: { dark: '#141613', light: '#ffffff' },
  rowOdd: { dark: '#1c1e1a', light: '#f5f7f3' },
};

interface RatesViewProps {
  operatingKey?: string;
  demandKey?: string;
  exportKey?: string;
  inventoryKey?: string;
  polymer: 'PP' | 'PS' | 'PVC';
}

const POLYMER_FIELD_OVERRIDES: Record<'PP' | 'PS' | 'PVC', { operating: string; demand: string; export: string; inventory: string }> = {
  PP: { operating: 'operating_rates', demand: 'demand_rates', export: 'export_rates', inventory: 'days_of_inventory' },
  PS: { operating: 'ps_operating', demand: 'ps_demand_rates', export: 'export_rates', inventory: 'ps_days' },
  PVC: { operating: 'operating_rates', demand: 'demand_rates', export: 'export_rates', inventory: 'days_of_inventory' },
};

const RatesView: React.FC<RatesViewProps> = ({
  operatingKey = 'operating_rates',
  demandKey = 'demand_rates',
  exportKey = 'export_rates',
  inventoryKey = 'days_of_inventory',
  polymer,
}) => {
  const [data, setData] = useState<any[]>([]);
  const [focusedDatasetIndex, setFocusedDatasetIndex] = useState<number | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const { isDark } = useTheme();
  const [isCompact, setIsCompact] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 749px)');
    const handler = (e: MediaQueryListEvent) => setIsCompact(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      let fetchedData: any[] = [];
      try {
        if (polymer === 'PP') {
          fetchedData = await getCachedPPRates();
        } else if (polymer === 'PS') {
          fetchedData = await getCachedPSRates();
        } else if (polymer === 'PVC') {
          fetchedData = await getCachedPVCRates();
        }
      } catch (error) {
        console.error('Error fetching rates data:', error);
      }
      setData(Array.isArray(fetchedData) ? fetchedData : []);
    };

    fetchData();
  }, [polymer]);

  const sorted = keepFirstEntryPerMonth(data).sort((a, b) => {
    const dateA = a.date || a.record_date || '';
    const dateB = b.date || b.record_date || '';
    return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
  });

  const guessKey = (rows: any[], candidateKeys: string[]) => {
    return candidateKeys.find((key) => rows.some((row) => row && row[key] !== null && row[key] !== undefined)) || candidateKeys[0];
  };

  const effectiveOperatingKey = guessKey(sorted, [operatingKey, POLYMER_FIELD_OVERRIDES[polymer].operating]);
  const effectiveDemandKey = guessKey(sorted, [demandKey, POLYMER_FIELD_OVERRIDES[polymer].demand]);
  const effectiveExportKey = guessKey(sorted, [exportKey, POLYMER_FIELD_OVERRIDES[polymer].export]);
  const effectiveInventoryKey = guessKey(sorted, [inventoryKey, POLYMER_FIELD_OVERRIDES[polymer].inventory]);

  const rs = useRsColors();

  const labels = sorted.map((d) => {
    try {
      return format(new Date((d.date || d.record_date) + 'T00:00:00'), 'MMM yyyy');
    } catch {
      return String(d.date || d.record_date).slice(0, 10);
    }
  });

  const operatingValues = sorted.map((d) => {
    const v = d[effectiveOperatingKey];
    return v !== null && v !== undefined ? Number(v) * 100 : null;
  });

  const demandValues = sorted.map((d) => {
    const v = d[effectiveDemandKey];
    return v !== null && v !== undefined ? Number(v) * 100 : null;
  });

  const inventoryValues = sorted.map((d) => {
    const v = d[effectiveInventoryKey];
    return v !== null && v !== undefined ? Number(v) : null;
  });

  const hoverLinePlugin = useMemo(() => createVerticalHoverLinePlugin(rs), [rs, isDark]);
  const areaPlugin = useMemo(() => createAreaBetweenLinesPlugin(), []);

  const createChartOptions = (limitedLabels: string[]) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        labels: { color: rs.offWhite, font: { size: 11 } },
        onClick: (_e: any, legendItem: any) => {
          const index = legendItem.datasetIndex;
          setFocusedDatasetIndex(focusedDatasetIndex === index ? null : index);
        },
      },
      tooltip: { mode: 'index' as const, intersect: false },
      datalabels: { display: false },
    },
    scales: {
      x: {
        ticks: {
          color: rs.offWhite,
          font: { size: 10 },
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          callback: function (_value: any, index: number) {
            return index % 6 === 0 ? limitedLabels[index] : '';
          },
        },
        grid: { color: rs.gridLine },
      },
      y: {
        position: 'left' as const,
        ticks: { color: rs.offWhite, callback: (v: any) => `${Math.round(v)}` },
        grid: { color: rs.gridLine },
        title: { display: true, text: 'Days of Inventory', color: rs.offWhite },
      },
      y1: {
        position: 'right' as const,
        ticks: { color: rs.offWhite, callback: (v: any) => `${Math.round(v)}%` },
        grid: { drawOnChartArea: false },
        title: { display: true, text: 'Percentage (%)', color: rs.offWhite },
      },
    },
  });

  const formatRate = (value: any) => {
    if (value === null || value === undefined || value === '') return '—';
    const num = Number(value);
    return Number.isNaN(num) ? '—' : `${Math.round(num * 100)}%`;
  };

  const formatInventory = (value: any) => {
    if (value === null || value === undefined || value === '') return '—';
    const num = Number(value);
    return Number.isNaN(num) ? '—' : `${Math.round(num)}`;
  };

  const handleColumnSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const getSortedRows = (rows: any[], effectiveDemandKey: string, effectiveOperatingKey: string, effectiveExportKey: string, effectiveInventoryKey: string) => {
    const sortedRows = [...rows];
    sortedRows.sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;

      const getDate = (row: any) => row.date || row.record_date || '';

      switch (sortColumn) {
        case 'date':
          aVal = getDate(a);
          bVal = getDate(b);
          break;
        case 'demand_rates':
          aVal = a[effectiveDemandKey] !== null ? Number(a[effectiveDemandKey]) : -Infinity;
          bVal = b[effectiveDemandKey] !== null ? Number(b[effectiveDemandKey]) : -Infinity;
          break;
        case 'operating_rates':
          aVal = a[effectiveOperatingKey] !== null ? Number(a[effectiveOperatingKey]) : -Infinity;
          bVal = b[effectiveOperatingKey] !== null ? Number(b[effectiveOperatingKey]) : -Infinity;
          break;
        case 'export_rates':
          aVal = a[effectiveExportKey] !== null ? Number(a[effectiveExportKey]) : -Infinity;
          bVal = b[effectiveExportKey] !== null ? Number(b[effectiveExportKey]) : -Infinity;
          break;
        case 'inventory':
          aVal = a[effectiveInventoryKey] !== null ? Number(a[effectiveInventoryKey]) : -Infinity;
          bVal = b[effectiveInventoryKey] !== null ? Number(b[effectiveInventoryKey]) : -Infinity;
          break;
        default:
          return 0;
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
    return sortedRows;
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return <ArrowUpDown size={14} className="opacity-40" />;
    }
    return sortDirection === 'asc' ?
      <ArrowUp size={14} /> :
      <ArrowDown size={14} />;
  };

  const exportCSV = () => {
    const headers = ['Date', 'Demand Rate (%)', 'Operating Rate (%)', 'Export Rate (%)', 'Days of Inventory'];
    const pct = (v: any) =>
      v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v)) ? (Number(v) * 100).toFixed(1) : '';
    const num = (v: any) =>
      v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v)) ? String(Math.round(Number(v))) : '';
    const rows = [...sorted].reverse();
    const dataRows = rows.map((row) =>
      [
        String(row.date || row.record_date || '').slice(0, 10),
        pct(row[effectiveDemandKey]),
        pct(row[effectiveOperatingKey]),
        pct(row[effectiveExportKey]),
        num(row[effectiveInventoryKey]),
      ].join(','),
    );
    const csvContent = [headers.join(','), ...dataRows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${polymer}-Rates-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 ${isCompact ? 'p-0 -mx-6' : ''}`}>
      <div className={`order-1 lg:order-2 flex flex-col gap-2 border border-rs-jet-light rounded-lg ${isCompact ? 'p-2' : 'p-4'} ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
        <ChartDateLimiter labels={labels} datasets={[{ data: operatingValues }, { data: demandValues }, { data: inventoryValues }]} yearLimit={1}>
          {(limitedLabels, limitedDatasets) => {
            const getOpacity = (index: number) => focusedDatasetIndex === null || focusedDatasetIndex === index ? 1 : 0.2;

            const limitedChartData = {
              labels: limitedLabels,
              datasets: [
                {
                  type: 'bar' as const,
                  label: 'Days of Inventory',
                  data: limitedDatasets[2]?.data || [],
                  backgroundColor: `rgba(132,204,22,${getOpacity(0) * 0.4})`,
                  borderColor: `rgba(132,204,22,${getOpacity(0) * 0.8})`,
                  borderWidth: 1,
                  borderRadius: 2,
                  yAxisID: 'y',
                },
                {
                  type: 'line' as const,
                  label: 'Operating Rate',
                  data: limitedDatasets[0]?.data || [],
                  borderColor: `rgba(0,102,255,${getOpacity(1)})`,
                  backgroundColor: 'transparent',
                  fill: false,
                  pointRadius: 0,
                  borderWidth: 4,
                  tension: 0.3,
                  spanGaps: true,
                  yAxisID: 'y1',
                },
                {
                  type: 'line' as const,
                  label: 'Demand Rate',
                  data: limitedDatasets[1]?.data || [],
                  borderColor: `rgba(255,140,0,${getOpacity(2)})`,
                  backgroundColor: 'transparent',
                  fill: false,
                  pointRadius: 0,
                  borderWidth: 4,
                  tension: 0.3,
                  spanGaps: true,
                  yAxisID: 'y1',
                },
              ],
            };
            const chartOptions = createChartOptions(limitedLabels);
            return (
              <div className={`${isCompact ? 'h-[500px]' : 'h-[650px]'}`}>
                <Chart type="bar" data={limitedChartData} options={chartOptions} plugins={[hoverLinePlugin, areaPlugin]} />
              </div>
            );
          }}
        </ChartDateLimiter>
      </div>
      <div className={`order-2 lg:order-1 overflow-x-auto border border-rs-jet-light rounded-lg bg-rs-off-black/70 ${isCompact ? 'h-auto' : 'h-[750px]'}`}>
         <div className="relative flex justify-center items-center py-3 bg-rs-off-black/80">
          <div
            className="px-4 py-1 rounded-md text-sm font-semibold text-center"
            style={{
              background: 'rgba(16,185,129,0.15)',
              color: 'rgba(16,185,129,0.7)',
            }}
          >
            Rates
          </div>
          <button
            onClick={exportCSV}
            disabled={sorted.length === 0}
            className="absolute right-3 flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium bg-rs-green text-black hover:bg-rs-dark-green disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Export"
          >
            <Download size={14} />
          </button>
        </div>
        <table className="min-w-full text-xs text-left border-collapse">
          <thead>
            <tr className="bg-rs-jet text-rs-off-white/70">
              <th
                className="px-3 py-2 cursor-pointer hover:bg-rs-jet-light transition-colors"
                onClick={() => handleColumnSort('date')}
              >
                <div className="flex items-center gap-1.5">
                  Date
                  {getSortIcon('date')}
                </div>
              </th>
              <th
                className="px-3 py-2 text-right cursor-pointer hover:bg-rs-jet-light transition-colors"
                onClick={() => handleColumnSort('demand_rates')}
              >
                <div className="flex items-center justify-end gap-1.5">
                  Demand Rate
                  {getSortIcon('demand_rates')}
                </div>
              </th>
              <th
                className="px-3 py-2 text-right cursor-pointer hover:bg-rs-jet-light transition-colors"
                onClick={() => handleColumnSort('operating_rates')}
              >
                <div className="flex items-center justify-end gap-1.5">
                  Operating Rate
                  {getSortIcon('operating_rates')}
                </div>
              </th>
              <th
                className="px-3 py-2 text-right cursor-pointer hover:bg-rs-jet-light transition-colors"
                onClick={() => handleColumnSort('export_rates')}
              >
                <div className="flex items-center justify-end gap-1.5">
                  Export Rate
                  {getSortIcon('export_rates')}
                </div>
              </th>
              <th
                className="px-3 py-2 text-right cursor-pointer hover:bg-rs-jet-light transition-colors"
                onClick={() => handleColumnSort('inventory')}
              >
                <div className="flex items-center justify-end gap-1.5">
                  Days of Inventory
                  {getSortIcon('inventory')}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {getSortedRows([...sorted].reverse(), effectiveDemandKey, effectiveOperatingKey, effectiveExportKey, effectiveInventoryKey).map((row, index) => {
              const rowBg = isDark
                ? (index % 2 === 0 ? SHADING.rowEven.dark : SHADING.rowOdd.dark)
                : (index % 2 === 0 ? SHADING.rowEven.light : SHADING.rowOdd.light);
              return (
                <tr key={`${String(row.date || row.record_date || index)}-${index}`} className="border-t border-rs-jet-light text-rs-off-white/70" style={{ backgroundColor: rowBg }}>
                  <td className="px-3 py-1.5">{format(new Date((row.date || row.record_date) + 'T00:00:00'), 'yyyy-MMM-dd')}</td>
                  <td className="px-3 py-1.5 text-right">{formatRate(row[effectiveDemandKey])}</td>
                  <td className="px-3 py-1.5 text-right">{formatRate(row[effectiveOperatingKey])}</td>
                  <td className="px-3 py-1.5 text-right">{formatRate(row[effectiveExportKey])}</td>
                  <td className="px-3 py-1.5 text-right">{formatInventory(row[effectiveInventoryKey])}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RatesView;
